import React, { useCallback, useEffect, useState } from 'react'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { Button } from '@/components/ui/button'

/**
 * A B2C organisation's subscriptions, read-only. Cancelling, refunding and card changes
 * happen in Stripe (the subscriber's portal or the partner's dashboard) and show up
 * here because the webhook recorded them.
 */

interface OrganisationSubscription {
  id: number
  userEmail: string | null
  userFirstName: string | null
  userLastName: string | null
  planName: string | null
  status: string
  cancelAtPeriodEnd: boolean
  renews: boolean
  currentPeriodEnd: string | null
  endedAt: string | null
  disputeStatus: string | null
  livemode: boolean
  stripeSubscriptionId: string | null
  createdAt: string
}

const GET_SUBSCRIPTIONS = gql`
  query getOrganisationSubscriptions($organisationId: Int!, $limit: Int, $offset: Int) {
    getOrganisationSubscriptions(organisationId: $organisationId, limit: $limit, offset: $offset) {
      totalCount
      subscriptions {
        id
        userEmail
        userFirstName
        userLastName
        planName
        status
        cancelAtPeriodEnd
        renews
        currentPeriodEnd
        endedAt
        disputeStatus
        livemode
        stripeSubscriptionId
        createdAt
      }
    }
  }
`

const PAGE_SIZE = 25

const STATUS_CLASS: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  past_due: 'bg-amber-100 text-amber-800',
  unpaid: 'bg-red-100 text-red-800',
  pending: 'bg-blue-100 text-blue-800',
  incomplete: 'bg-blue-100 text-blue-800',
  canceled: 'bg-gray-200 text-gray-600',
  expired: 'bg-gray-200 text-gray-600',
}

const SubscriptionsList: React.FC<{ organisationId: number }> = ({ organisationId }) => {
  const [rows, setRows] = useState<OrganisationSubscription[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loadingPage, setLoadingPage] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchPage = useCallback(async () => {
    try {
      setLoadingPage(true)
      setError(null)
      const response = await authorizedGraphQLRequest<{
        getOrganisationSubscriptions: { subscriptions: OrganisationSubscription[]; totalCount: number }
      }>(GET_SUBSCRIPTIONS, { organisationId, limit: PAGE_SIZE, offset })
      setRows(response.getOrganisationSubscriptions?.subscriptions || [])
      setTotalCount(response.getOrganisationSubscriptions?.totalCount || 0)
    } catch (err: any) {
      setError(err?.response?.errors?.[0]?.message || 'Failed to load subscriptions')
    } finally {
      setLoadingPage(false)
    }
  }, [organisationId, offset])

  useEffect(() => {
    fetchPage()
  }, [fetchPage])

  useEffect(() => {
    setOffset(0)
  }, [organisationId])

  const page = Math.floor(offset / PAGE_SIZE) + 1
  const pages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="font-semibold text-gray-800">Subscriptions ({totalCount})</h4>
        <p className="text-xs text-gray-500">Subscribers cancel in Stripe's portal; the partner can also cancel or refund in their Stripe Dashboard.</p>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 text-sm text-red-700 border border-red-100">{error}</div>}

      {rows.length === 0 && !loadingPage ? (
        <div className="text-center text-gray-500 py-6 bg-gray-50 rounded-xl">No subscriptions yet</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                <th className="py-2 pr-4 font-semibold">Started</th>
                <th className="py-2 pr-4 font-semibold">User</th>
                <th className="py-2 pr-4 font-semibold">Plan</th>
                <th className="py-2 pr-4 font-semibold">Status</th>
                <th className="py-2 pr-4 font-semibold">Period end</th>
                <th className="py-2 pr-4 font-semibold">Dispute</th>
                <th className="py-2 font-semibold">Stripe subscription</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-gray-100 last:border-0">
                  <td className="py-2 pr-4 whitespace-nowrap text-gray-700">{new Date(row.createdAt).toLocaleDateString()}</td>
                  <td className="py-2 pr-4 text-gray-700">
                    <span className="block truncate max-w-48">{[row.userFirstName, row.userLastName].filter(Boolean).join(' ') || row.userEmail}</span>
                  </td>
                  <td className="py-2 pr-4 text-gray-700">
                    {row.planName}
                    {row.renews ? null : <span className="ml-1 text-xs text-gray-500">one-time</span>}
                  </td>
                  <td className="py-2 pr-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_CLASS[row.status] || 'bg-gray-100 text-gray-600'}`}>{row.status.replace('_', ' ')}</span>
                    {row.cancelAtPeriodEnd && row.status === 'active' && <span className="ml-1 text-xs text-gray-500">cancels</span>}
                    {row.livemode ? null : <span className="ml-1 text-xs text-gray-400">test</span>}
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap text-gray-700">
                    {row.endedAt ? new Date(row.endedAt).toLocaleDateString() : row.currentPeriodEnd ? new Date(row.currentPeriodEnd).toLocaleDateString() : '—'}
                  </td>
                  <td className="py-2 pr-4 text-gray-700">{row.disputeStatus || '—'}</td>
                  <td className="py-2 font-mono text-xs text-gray-500">{row.stripeSubscriptionId || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">
            Page {page} of {pages}
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={offset === 0 || loadingPage} onClick={() => setOffset((current) => Math.max(0, current - PAGE_SIZE))}>
              Previous
            </Button>
            <Button size="sm" variant="outline" disabled={offset + PAGE_SIZE >= totalCount || loadingPage} onClick={() => setOffset((current) => current + PAGE_SIZE)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default SubscriptionsList
